import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PrintService } from '../print.service';
import { ExamsService } from '../exams.service';
import { ClinicExamField, normalizeField } from '../clinic-exam/clinic-exam.model';

/**
 * Print page for a Clinics exam composed in the exam builder (GUS-88).
 * pdf.js routes here via printPrefix='clinicexam' when the exam's template is
 * of kind 'builder'.
 *
 * It uses the exam-scoped definition, so a field the admin later removed still
 * prints as long as this exam holds a value for it — a signed exam's printout
 * can never change retroactively.
 */
@Component({
  selector: 'app-clinic-exam-print',
  templateUrl: './clinic-exam-print.component.html',
  styleUrls: ['./clinic-exam-print.component.scss'],
  standalone: false
})
export class ClinicExamPrintComponent implements OnInit {
  examID: number;
  PID: number;
  now: Date;

  Direction = 'ltr';
  typeOfPrint = 'simple';
  loaded = false;
  hasImages = false;
  imPerPage = 2;
  altHeader: SafeHtml;
  showHeader = true;

  examTitle = '';
  fields: ClinicExamField[] = [];
  private values: { [name: string]: string } = {};
  dataLoaded = false;

  constructor(
    private route: ActivatedRoute,
    public print: PrintService,
    public es: ExamsService,
    public translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {
    this.now = new Date();
  }

  ngOnInit() {
    this.translate.use(this.route.snapshot.params.LANG);
    if (this.route.snapshot.params.LANG == 'he-IL' || this.route.snapshot.params.LANG == 'ar-JO')
      this.Direction = 'rtl';
    this.typeOfPrint = this.route.snapshot.params.style;

    const code = this.route.snapshot.params['code'];
    this.examID = this.route.snapshot.params.examid;
    this.PID = this.route.snapshot.params.PID;

    if (this.route.snapshot.params['images'] == 'y') {
      this.hasImages = true;
      this.imPerPage = this.route.snapshot.params['perpage'];
    }
    if (this.route.snapshot.params['perpage'] == 'All') {
      this.hasImages = true;
      this.imPerPage = 2;
      this.print.allImages = true;
    } else {
      this.print.allImages = false;
    }
    if (this.route.snapshot.params.hasOwnProperty('showheader'))
      this.showHeader = this.route.snapshot.params['showheader'] == 'y';

    // loadExam -> getPatient establishes the puppeteer session from :code
    this.print.loadExam(this.examID, this.PID, this.hasImages, code);
    this.print.loadMsg.subscribe(() => {
      if (this.print.env['htmlheader']) {
        this.altHeader = this.sanitizer.bypassSecurityTrustHtml(this.print.env['htmlheader']);
      }
      this.loadDefinition();
    });
  }

  private loadDefinition() {
    try {
      this.es.getClinicExamDefinition(this.examID).subscribe(
        (data: any) => {
          this.examTitle = (data && data.Title) || '';
          this.fields = ((data && data.fields) || []).map((f) => normalizeField(f));
          this.loadValues();
        },
        (err) => { console.log(err); this.finish(); }
      );
    } catch (e) {
      console.log(e);
      this.finish();
    }
  }

  private loadValues() {
    try {
      this.es.getClinicExamValues(this.examID).subscribe(
        (data: any) => {
          for (const row of data || []) {
            this.values['f' + row.FieldID + (row.ValueKey ? '_' + row.ValueKey : '')] = row.Value;
          }
          this.finish();
        },
        (err) => { console.log(err); this.finish(); }
      );
    } catch (e) {
      console.log(e);
      this.finish();
    }
  }

  private finish() {
    this.dataLoaded = true;
    this.loaded = true;
  }

  // ---------- template helpers ----------

  value(field: ClinicExamField, valueKey = ''): string {
    return this.values['f' + field.FieldID + (valueKey ? '_' + valueKey : '')] || '';
  }

  rowValue(field: ClinicExamField, i: number, comment = false): string {
    return this.value(field, 'r' + i + (comment ? 'c' : ''));
  }

  /** The chosen side of a yes/no toggle, as its label. */
  toggleLabel(field: ClinicExamField, valueKey = ''): string {
    const raw = this.value(field, valueKey);
    if (raw === '' || raw === null || raw === undefined) return '';
    const left = field.Config.leftValue === undefined ? 0 : field.Config.leftValue;
    const right = field.Config.rightValue === undefined ? 1 : field.Config.rightValue;
    if (String(raw) === String(left)) return field.Config.left || 'Normal';
    if (String(raw) === String(right)) return field.Config.right || 'Abnormal';
    return '';
  }

  /** A field is printed only when the doctor actually put something in it. */
  hasContent(field: ClinicExamField): boolean {
    switch (field.FieldType) {
      case 'header':
        return true;
      case 'yesno':
        return !!this.toggleLabel(field) || !!this.value(field, 'c');
      case 'table':
        return (field.Config.rows || []).some(
          (r, i) => !!this.rowValue(field, i) || !!this.rowValue(field, i, true)
        );
      default:
        return !!this.value(field);
    }
  }

  /** The first summary field that actually prints - the diagnoses go just above it,
   * so a Clinics printout keeps the same order as every other exam type. */
  firstPrintedSummary(): ClinicExamField | null {
    return this.fields.find((f) => f.FieldType === 'summary' && this.hasContent(f)) || null;
  }

  anyContent(): boolean {
    return this.fields.some((f) => f.FieldType !== 'header' && this.hasContent(f));
  }
}
